// Every word the open chat says on the landing page (#2725; founder pick B
// of the "Ask Anything" mocks, 2026-10-01: https://claude.ai/artifact/8gFZK4LWKcemFFocmZBEB9).
// The seed questions are the ONE list in astraChatSeeds.json, which the
// guardrail evals also read (each was checked against live data).
//
// Honesty rules for this file: a nudge names only what signing in does
// TODAY, at a route that exists; no price, plan or credit words anywhere
// (noCreditTalkGuard.test.js); the daily number always comes from the
// server (free_questions), never from here.
import SEEDS from './astraChatSeeds.json';
import { QUESTIONS_KEPT, WHO_ASKS } from './astraLite';

export const CHAT_TABS = SEEDS.tabs;

export const CHAT = {
    heading: 'Ask Astra. She never logs off.',
    // the count is the live knowledge base's (UNIVERSITY_COUNT), filled in by the page
    sub: (n) => `Deadlines, testing, cost and majors at ${n} universities, from each school's own data, with the source on every answer.`,
    panelName: 'Astra',
    panelLine: 'Ask anything, no sign-in',
    personalize: 'Make it about me',
    ariaLabel: 'Ask Astra anything about college, for example: Is UCLA test-blind?',
    go: 'Ask',
    // Under the Ask box (founder, 2026-10-04): the questions are kept now (#3063).
    saved: "Questions are kept to improve answers. Don't include your name.",
    tabsLabel: 'Topics',
    thinking: 'Astra is reading...',
    error: 'Something went wrong on our side. Please try again in a moment.',
    busy: "I'm getting a lot of questions right now. Please try again in a little while.",
    // Shown only in the last five questions of the day (founder decision).
    counter: (left, total) => `${left} of ${total} free ${total === 1 ? 'question' : 'questions'} left today`,
    bandsAsk: 'Pick the range closest to your family income:',
    sourcesLabel: 'Sources',
    noLink: 'no link',
    tableNote: 'Drawn from the same data as the answer.',
    // Literally true (#2903): per-answer counts and the daily topic and school
    // totals keep no words and no identity. Who asks is counted only from a
    // picture ("Make it about me" opened before any question; the plain
    // questions count under 'unknown', astra_lite._open_session), so the
    // condition stays. WHO_ASKS is the one list, pinned to the server's labels.
    notice: `We count the kinds of questions asked and the schools they name. If you describe yourself, we also keep totals of ${WHO_ASKS}. ${QUESTIONS_KEPT}`,
    // Under the picture form (#2896): back to the question box, the conversation untouched.
    notNow: 'Not now, keep asking questions',
    // Beside Build my picture on the same step (#5237, founder 2026-10-23):
    // sign in without describing yourself first.
    signinInstead: 'Sign in instead',
    // The kept conversation (#2809): clears this tab's thread, never the day's count.
    startOver: 'Start over',
    startOverLabel: 'Start over: clear this conversation',
    personalizeTitle: 'Make it about me',
    // The personal-read card under an odds or cost answer (#3262, Set 2 pick
    // A of the Astra entry mocks, the mock's words). It asks for a grade and a
    // GPA only, never for scores: the school may be test-blind (product
    // invariant 4; the old line asked every school's visitor for scores).
    offerTitle: 'Want this checked against your own grades?',
    // Under a cost answer "this" would be the cost, so the line names the read.
    offerTitleCost: (name) => `Want to see where you stand at ${name}?`,
    offerLine: (name) => `Free, and you don't sign in. Tell Astra your grade and GPA and she reads them against ${name}'s admitted class.`,
    offerButton: 'Check it with my grades',
    // The nudge's read button (#5229): the same words as the card's, so the
    // free read is called one thing wherever the chat offers it.
    nudgeReadButton: 'Check it with my grades',
};

// After a factual answer about a school (#5229, a follow-up to #5195's A;
// founder, 2026-10-23, on the NYU deadline answer's box: the sign-in line
// drew 1 click in 57 showings), the box leads with the free read as the
// next step. One line per topic, and each names ONLY what the free read
// gives a signed-out visitor: the band, the school's rate and its testing
// policy (docs/specs/signup-moments/product.md, section 15). Never the
// reasons behind the band (the #3480 lock keeps them for an account), never
// other applicants' outcomes (list-gated, product invariant 17), no credit
// or meter words (invariant 1). "Free" is true: the read charges nothing.
// `s` is the school's name; with no school there is no read to lead with.
export const READ_TAIL = 'Add your grade and GPA, free, no sign-in.';
const READ_LEAD = {
    deadlines: (s) => `Where would you stand at ${s}? ${READ_TAIL}`,
    cost: (s) => `Is ${s} a Reach or a Target for you? ${READ_TAIL}`,
    odds: (s) => `${s}'s rate is everyone's average. See your own band there. ${READ_TAIL}`,
    majors: (s) => `Is ${s} a Reach or a Target for you? ${READ_TAIL}`,
    essays: (s) => `Writing for ${s}? First see where you stand there. ${READ_TAIL}`,
    news: (s) => `Reading about ${s}? See where you would stand there. ${READ_TAIL}`,
};
export const READ_LEAD_KEYS = Object.keys(READ_LEAD);

/** The read line that leads the nudge for `key` about `school`, or null
 *  (no school, or a topic the free read does not fit: record, summer, the
 *  results ask). */
export function readLeadFor(key, school) {
    const f = school ? READ_LEAD[key] : null;
    return f ? f(school) : null;
}

// The composer's typing loop: real questions, each one a seed or close to it.
export const QUESTION_SAMPLES = [
    { key: 'q1', text: 'Is UCLA test-blind, or just test-optional?' },
    { key: 'q2', text: 'When is the early decision deadline at NYU?' },
    { key: 'q3', text: 'What do families like ours pay at Georgia Tech after aid?' },
    { key: 'q4', text: 'Can I get into computer science directly at UIUC?' },
];

// The light nudge after the first useful answer, by the answer's topic. Each
// line is a thing the signed-in product does today, at the route named.
// `school` is the school the answer was about, when there was one.
const NUDGE = {
    deadlines: {
        line: (s) => (s ? `Sign in and ${s}'s dates go on your own roadmap, paced to your year, with a weekly email of what's due next.`
            : "Sign in and every deadline on your list goes on one roadmap, paced to your year, with a weekly email of what's due next."),
        route: '/roadmap',
    },
    cost: {
        line: (s) => (s ? `Sign in to set ${s} beside every school on your list, with what families in your range paid at each.`
            : 'Sign in to see every school on your list side by side, with what families in your range paid at each.'),
        route: '/financial-aid',
    },
    odds: {
        line: (s) => (s ? `Sign in for your fit and odds at ${s}, for your major, read from your whole record.`
            : 'Sign in for your fit and odds at every school on your list, for your major, read from your whole record.'),
        route: '/launchpad',
    },
    majors: {
        line: () => 'Sign in for the majors your record supports at each school on your list.',
        route: '/major-map',
    },
    essays: {
        line: (s) => (s ? `Sign in and Astra coaches each ${s} prompt with you. She asks; you write every sentence.`
            : 'Sign in and Astra coaches each prompt with you. She asks; you write every sentence.'),
        route: '/roadmap',
    },
    summer: {
        line: () => 'Sign in for programs matched to your grade and interests, with their deadlines on your roadmap.',
        route: '/programs',
    },
    record: {
        line: () => 'Sign in and Astra reads your whole record before every answer, and picks up where you left off.',
        route: '/launchpad',
    },
    // #3600: past applicants' self-reported results open on the Launchpad
    // for a school on the student's own list, never before; the server sends
    // this key with its answer to a question asking for them.
    results: {
        line: (s) => (s ? `Sign in and add ${s} to your list to see what past applicants reported there.`
            : 'Sign in and add a school to your list to see what past applicants reported there.'),
        route: '/launchpad',
    },
};

const TOPIC_NUDGE = {
    deadlines: 'deadlines', timeline: 'deadlines',
    cost_aid: 'cost', aid_process: 'cost',
    school_facts: 'odds', test_policy: 'odds', school_odds: 'odds', list_building: 'odds',
    major_choice: 'majors', essays: 'essays',
    summer_programs: 'summer', activities: 'summer',
};

// The one shape the chat draws: the sign-in line and its route, and (#5229)
// the read line that leads the box when the free read fits the topic and
// the answer named a school, with the school's name for the read's preset.
function nudgeShape(key, school) {
    const n = NUDGE[key];
    const s = school || null;
    return { key, line: n.line(s), route: n.route, read: readLeadFor(key, s), school: s };
}

// A nudge the server named for its answer (`nudge` on the response), by key.
export function nudgeByKey(key, school) {
    return NUDGE[key] ? nudgeShape(key, school) : null;
}

export function nudgeFor(topic, school) {
    return nudgeShape(TOPIC_NUDGE[topic] || 'record', school);
}

// The card at the daily limit: what signing in does for the topics they
// asked (the two most asked), then the founder's "whole record" line (true
// only after sign-in, which is why it lives here), then the four Build
// actions. No price, plan or credit words.
const LIMIT_ITEMS = {
    deadlines: 'Every deadline on your list on one roadmap, with a weekly email of what is due',
    cost: 'Your cost beside every school on your list, by your income range',
    odds: 'Your fit and odds at each school, for your major',
    majors: 'The majors your record supports at each school on your list',
    essays: 'An essay coach for each prompt, in your own voice',
    summer: 'Summer programs matched to your grade and interests',
};
export const LIMIT_MEMORY = 'Astra reads your whole record before every answer, and picks up where you left off';

export function limitItems(topics) {
    const keys = [];
    for (const t of topics || []) {
        const k = TOPIC_NUDGE[t];
        if (k && LIMIT_ITEMS[k] && !keys.includes(k)) keys.push(k);
        if (keys.length === 2) break;
    }
    return [...keys.map((k) => LIMIT_ITEMS[k]), LIMIT_MEMORY];
}

export const LIMIT = {
    // `total` unknown: no number is invented
    title: (total) => (Number.isInteger(total) && total > 0
        ? `That's your ${total} free ${total === 1 ? 'question' : 'questions'} for today.`
        : "That's your free questions for today."),
    lead: 'Sign in and Astra keeps going, from your own record:',
    // The Build names come from the page's visible tools (their flags), so a
    // tool that is not open is never promised here.
    buildLabel: 'And build something of your own',
    small: (total) => (Number.isInteger(total) && total > 0
        ? `Or come back tomorrow for ${total} more.`
        : 'Or come back tomorrow for more.'),
};
